<template>
  <div class="input-group">
    <fieldset>
      <legend v-if="$slots.default"><slot /></legend>
      <component
        :is="componentType"
        v-for="option in inputOptions"
        :id="`${id}-${option.id}`"
        :key="option.id"
        :option-id="stringifyOptionId(option.id)"
        :value="option.value"
        :checked="option.checked"
        :name="option.name"
        :is-other="option.isOther"
        :disabled="limitHasBeenMet && !option.checked"
        :limit="limit"
        :other-value="option.otherValue"
        @input="update"
      >
        {{ (option.label || option.value) | decodeHtml }}
      </component>
    </fieldset>
  </div>
</template>

<script>
import RadioButton from './RadioButton.vue';
import CheckboxComponent from './CheckboxComponent.vue';

const components = {
  radio: RadioButton,
  checkbox: CheckboxComponent,
};
const allowedTypes = ['radio', 'checkbox'];

export default {
  name: 'InputGroup',
  props: {
    id: {
      type: String,
      required: true,
    },
    /**
     * The type of input to be dynamically rendered. Valid options ['checkbox', 'radio'].
     */
    type: {
      type: String,
      required: true,
      validation: (value) => allowedTypes.includes(value),
    },
    /**
     * An array of objects providing relevant data for the inputs within the group
     */
    inputOptions: {
      type: Array,
      required: true,
      validator: (options) => options.every((option) => 'value' in option && 'name' in option),
    },
    limit: {
      type: Number,
      default: 0,
    },
    valueNeedsToBeAnArray: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedOptions: [],
    };
  },
  computed: {
    componentType() {
      return components[this.type];
    },
    limitHasBeenMet() {
      if (this.limit > 0) {
        return this.selectedOptions.length >= this.limit;
      }
      return false;
    },
  },
  created() {
    if (this.valueNeedsToBeAnArray || this.type === 'checkbox') {
      this.selectedOptions = this.inputOptions.filter((option) => (
        option.checked
      ));
    }
  },
  methods: {
    update(value) {
      if (this.limit > 0 && this.type === 'checkbox') {
        this.handleSelectedOptions(value);
      }
      if (this.valueNeedsToBeAnArray && this.type === 'checkbox') {
        this.handleSelectedOptions(value);
        this.$emit('input', this.selectedOptions);
        return;
      }

      this.$emit('input', value);
    },
    handleSelectedOptions(value) {
      const indexOfPreviousSelection = this.selectedOptions.findIndex(
        (option) => option.id === value.optionId,
      );
      // make this consitently id instead of optionId
      const newValue = { ...value, id: value.optionId };
      if (indexOfPreviousSelection >= 0) {
        if (value.checked) {
          this.selectedOptions.splice(indexOfPreviousSelection, 1, newValue);
        } else {
          this.selectedOptions.splice(indexOfPreviousSelection, 1);
        }
      } else {
        this.selectedOptions.push(newValue);
      }
    },
    stringifyOptionId(id) {
      if (!id) { return; }
      if (typeof id === 'string') {
        // eslint-disable-next-line consistent-return
        return id;
      }
      // eslint-disable-next-line consistent-return
      return id.toString();
    },
  },
};
</script>

<docs>
The input group renders an array of inputs, either checkboxes or radio buttons,
with options for each input provided as inputOptions.

</docs>
